import type { Session } from "./types";

const CLE_SESSION = "tickboss-production-session";

export class ErreurApi extends Error {
  constructor(
    message: string,
    public readonly statut: number
  ) {
    super(message);
  }
}

export function lireSession(): Session | null {
  const valeur = localStorage.getItem(CLE_SESSION);
  if (!valeur) return null;
  try {
    const session = JSON.parse(valeur) as Session;
    if (new Date(session.expiration).getTime() <= Date.now()) {
      localStorage.removeItem(CLE_SESSION);
      return null;
    }
    return session;
  } catch {
    localStorage.removeItem(CLE_SESSION);
    return null;
  }
}

export function enregistrerSession(session: Session | null): void {
  if (session) {
    localStorage.setItem(CLE_SESSION, JSON.stringify(session));
  } else {
    localStorage.removeItem(CLE_SESSION);
  }
}

export async function appelerApi<T>(
  chemin: string,
  options: RequestInit = {},
  authentifier = false
): Promise<T> {
  const enTetes = new Headers(options.headers);
  if (options.body && !(options.body instanceof FormData)) {
    enTetes.set("Content-Type", "application/json");
  }
  if (authentifier) {
    const session = lireSession();
    if (!session) throw new ErreurApi("Votre session a expiré.", 401);
    enTetes.set("Authorization", `Bearer ${session.jeton}`);
  }
  const reponse = await fetch(`/api/v1${chemin}`, { ...options, headers: enTetes });
  if (!reponse.ok) {
    let message = "Une erreur est survenue.";
    try {
      const erreur = (await reponse.json()) as { detail?: string | Array<{ msg: string }> };
      if (typeof erreur.detail === "string") {
        message = erreur.detail;
      } else if (Array.isArray(erreur.detail)) {
        message = erreur.detail.map((element) => element.msg).join(" · ");
      }
    } catch {
      message = `Erreur HTTP ${reponse.status}`;
    }
    throw new ErreurApi(message, reponse.status);
  }
  if (reponse.status === 204) return undefined as T;
  return (await reponse.json()) as T;
}

export function corpsJson(donnees: unknown): string {
  return JSON.stringify(donnees);
}

async function chargerRessourceAuthentifiee(chemin: string): Promise<Blob> {
  const session = lireSession();
  if (!session) throw new ErreurApi("Votre session a expiré.", 401);
  const reponse = await fetch(`/api/v1${chemin}`, {
    headers: { Authorization: `Bearer ${session.jeton}` }
  });
  if (!reponse.ok) {
    throw new ErreurApi(`Téléchargement impossible (HTTP ${reponse.status}).`, reponse.status);
  }
  return reponse.blob();
}

export async function telechargerRessourceApi(
  chemin: string,
  nomFichier: string
): Promise<void> {
  const blob = await chargerRessourceAuthentifiee(chemin);
  const url = URL.createObjectURL(blob);
  const lien = document.createElement("a");
  lien.href = url;
  lien.download = nomFichier;
  lien.click();
  URL.revokeObjectURL(url);
}

export async function ouvrirRessourceApi(chemin: string): Promise<void> {
  const blob = await chargerRessourceAuthentifiee(chemin);
  const url = URL.createObjectURL(blob);
  const lien = document.createElement("a");
  lien.href = url;
  lien.target = "_blank";
  lien.rel = "noopener";
  lien.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
